import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../lib/utils';
import logo from '../assets/logo.png';
import { isTestMode } from '../services/paymentApi';

const menuItems = [
  { label: 'Inicio', id: 'inicio' },
  { label: 'Nosotros', id: 'nosotros' },
  { label: 'Cursos', id: 'cursos' },
  { label: 'Aula Virtual', id: 'aula' },
  { label: 'Contacto', id: 'contacto' },
];

export default function Header() {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Check if test mode banner is shown (adds 36px height)
  const hasTestBanner = isTestMode();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      // Account for test banner height when scrolling
      const offset = hasTestBanner ? 116 : 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    } else {
      // If element not found (e.g., on payment result page), navigate to home
      window.location.href = id === 'inicio' ? '/' : `/#${id}`;
    }
    setMobileOpen(false);
  };

  return (
    <header
      className={cn(
        "fixed left-0 right-0 z-50 transition-all duration-300",
        hasTestBanner ? "top-9" : "top-0",
        isScrolled ? "bg-white/80 backdrop-blur-md shadow-sm py-2" : "bg-transparent py-4"
      )}
    >
      <div className="container mx-auto px-4 flex items-center justify-between">
        {/* Logo */}
        <div
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => scrollToSection('inicio')}
        >
          <img src={logo} alt="Instituto Valparaíso" className="h-10 w-auto rounded-lg" />
          <span className={cn(
            "text-xl font-bold font-heading hidden sm:block transition-colors",
            isScrolled ? "text-primary" : "text-white"
          )}>
            Instituto Valparaíso
          </span>
        </div>

        {/* Desktop Menu */}
        <nav className="hidden md:flex items-center gap-8">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className={cn(
                "text-sm font-medium transition-colors hover:text-accent",
                isScrolled ? "text-slate-700" : "text-white/90 hover:text-white"
              )}
            >
              {item.label}
            </button>
          ))}
          <button
            onClick={() => navigate('/cursos')}
            className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-6 py-2.5 rounded-full font-semibold transition-all hover:scale-105 shadow-lg shadow-blue-500/30"
          >
            Inscríbete
          </button>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2 text-current"
          onClick={() => setMobileOpen(true)}
        >
          <Menu className={cn("h-6 w-6", isScrolled ? "text-primary" : "text-white")} />
        </button>
      </div>

      {/* Mobile Drawer - Rendered via Portal to avoid z-index/transform issues */}
      {createPortal(
        <AnimatePresence>
          {mobileOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setMobileOpen(false)}
                className="fixed inset-0 bg-black/50 z-[9998] md:hidden"
              />
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                className="fixed top-0 right-0 bottom-0 w-[280px] bg-white z-[9999] shadow-2xl md:hidden flex flex-col"
              >
                <div className="p-4 flex justify-end">
                  <button onClick={() => setMobileOpen(false)} className="p-2 text-slate-500 hover:text-slate-800">
                    <X className="h-6 w-6" />
                  </button>
                </div>
                <div className="flex flex-col p-6 gap-4">
                  {menuItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className="text-lg font-medium text-slate-700 hover:text-primary text-left py-2 border-b border-slate-100"
                    >
                      {item.label}
                    </button>
                  ))}
                  <button
                    onClick={() => { setMobileOpen(false); navigate('/cursos'); }}
                    className="mt-4 bg-primary text-white py-3 rounded-lg font-bold shadow-lg"
                  >
                    Inscríbete Ahora
                  </button>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </header>
  );
}
